/**
 * The persisted shape of upload-panel batches and the rules for writing and
 * reading it. Kept free of store/network imports so the standalone test runner
 * can exercise it (Notifications.ts pulls in the whole store graph).
 */
import type { FolderPath } from '@lib/library/folderPath';
import type {
  AddMediaViaUrlNotification,
  FailedUpload,
  Notification,
  UploadFileNotification,
} from '@stores/Notifications';
import {
  UPLOAD_FAILURE_CATEGORIES,
  type UploadFailureCategory,
} from '@stores/UploadManager/uploadFailure';

export const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
export const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export interface PersistedEntry {
  /** Notification type. Undefined means legacy 'add-media-via-url'. */
  type?: 'add-media-via-url' | 'upload-file';
  libraryId: string;
  /** Library folder the batch was filed in. Undefined for entries persisted before library
   * folders existed, whose media all went to the root. */
  folderPath?: FolderPath;
  mediaIds: string[];
  /** Upload-file batches only. Undefined for entries persisted before it existed, and read
   * as no failures. */
  failedUploads?: FailedUpload[];
  thumbnailUrl: string | null;
  /** Optional. Drives the BatchCard's faked stack-card backdrop on multi-file
   * batches. Undefined for legacy entries — restored as null. */
  secondaryThumbnailUrl?: string | null;
  /** Optional. Seeds the BatchCard's gradient placeholders so colours match
   * the library grid for the same media. Undefined for legacy entries —
   * restored as null and the BatchCard falls back to notification.id. */
  gradientSeed?: string | null;
  secondaryGradientSeed?: string | null;
  /** Display title. Undefined for legacy entries — falls back to library name. */
  title?: string;
  /** When the notification was first created. */
  timestamp: number;
  /** When progress reached 1, or null if still active. */
  completedAt: number | null;
  /** Whether the user has seen this notification. Undefined means unread (legacy). */
  read?: boolean;
}

function isFailedUpload(value: unknown): value is FailedUpload {
  const v = value as Partial<FailedUpload> | null;
  return (
    typeof v?.fileName === 'string' &&
    typeof v.reason === 'string' &&
    UPLOAD_FAILURE_CATEGORIES.includes(v.category as UploadFailureCategory)
  );
}

/** A batch is worth keeping across a reload when something reached the server or
 * failed on the way. Cancellation isn't recorded on the batch, so a cancel-only
 * batch is dropped. */
function hasOutcome(batch: { mediaIds: string[]; failedUploads: FailedUpload[] }): boolean {
  return batch.mediaIds.length > 0 || batch.failedUploads.length > 0;
}

export function toPersistedEntries(notifications: Notification[], now: number): PersistedEntry[] {
  return notifications
    .filter(
      (n): n is AddMediaViaUrlNotification | UploadFileNotification =>
        (n.type === 'add-media-via-url' || (n.type === 'upload-file' && hasOutcome(n))) &&
        // Drop entries older than 30 days regardless of state, so orphaned
        // never-completed batches don't accumulate forever.
        now - n.timestamp < THIRTY_DAYS_MS &&
        // Keep active notifications and completed ones younger than 24h
        (n.completedAt === null || now - n.completedAt < TWENTY_FOUR_HOURS_MS),
    )
    .map((n) => ({
      type: n.type as 'add-media-via-url' | 'upload-file',
      libraryId: n.libraryId,
      folderPath: n.folderPath,
      mediaIds: n.mediaIds,
      ...(n.type === 'upload-file' && { failedUploads: n.failedUploads }),
      thumbnailUrl: n.thumbnailUrl,
      secondaryThumbnailUrl: n.secondaryThumbnailUrl,
      gradientSeed: n.gradientSeed,
      secondaryGradientSeed: n.secondaryGradientSeed,
      title: n.title,
      timestamp: n.timestamp,
      completedAt: n.completedAt,
      read: n.read,
    }));
}

/** The entries of a parsed storage value that restore can use. Throws on an array
 * holding `null`, as reading it always has; the caller treats that as empty. */
export function parseStoredEntries(parsed: unknown, now: number): PersistedEntry[] {
  if (!Array.isArray(parsed)) return [];
  return parsed.filter(
    (e: any) =>
      typeof e.libraryId === 'string' &&
      Array.isArray(e.mediaIds) &&
      e.mediaIds.every((id: any) => typeof id === 'string') &&
      typeof e.timestamp === 'number' &&
      now - e.timestamp < THIRTY_DAYS_MS,
  );
}

/** The failures restore puts back on the batch, or null when the entry has nothing
 * left to show. Malformed failure records are dropped. */
export function restorableFailures(entry: PersistedEntry): FailedUpload[] | null {
  const failedUploads = (entry.failedUploads ?? []).filter(isFailedUpload);
  return hasOutcome({ mediaIds: entry.mediaIds, failedUploads }) ? failedUploads : null;
}
